import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import {
  microAnimationFor,
  type MicroAnimationType,
} from "./animations";

/**
 * Micro-interaction = 「アクションへの即時フィードバックとして、短く現れて
 * 自然に消えるテキスト演出」。
 *
 * 起動方法（2 通り。併用可）:
 * - `trigger` を渡す: その要素のクリックを契機に演出を再生する。
 *   既存の「ボタン押下 → ありがとう！」のような uncontrolled な使い方。
 * - `show` を渡す: 外部 state から起動する controlled モード。
 *   `false → true` の遷移を検知して 1 回再生する（長押し / 自動完了など、
 *   click 以外のトリガを持つ UI 向け）。
 *
 * 表示形式:
 * - 既定: 親要素の **絶対配置の吹き出し**（カプセル + 1px white border）。
 *   `placement` で trigger に対する位置を指定する。
 * - `inline`: 親フロー内にそのまま文字を置く。背景なし・soremo-blue 文字。
 *   ボタン直下のキャプション位置に重ねたい場面で使う。
 */

interface MicroInteractionProps {
  /** 表示するテキスト */
  text: string;
  /** クリック起動用の trigger 要素。`show` と併用も可。 */
  trigger?: React.ReactElement<{
    onClick?: React.MouseEventHandler<HTMLElement>;
  }>;
  /** Controlled 起動。false → true の遷移で 1 回再生する。 */
  show?: boolean;
  /** trigger に対する位置（吹き出しモードのみ）。既定 `top` */
  placement?: "top" | "bottom" | "left" | "right";
  /** trigger との距離。既定 `8px` */
  offset?: number;
  /** 自動で消えるまでの時間。既定 `2000ms` */
  duration?: number;
  /** アニメーション種別。既定 `scale` */
  animationType?: MicroAnimationType;
  /** material-symbols のアイコン名（テキスト左に表示） */
  icon?: string;
  /** フォントサイズ。既定 `11px` */
  fontSize?: string;
  /** テキスト色。既定: 吹き出し `#fff` / inline `var(--soremo-blue)` */
  color?: string;
  /**
   * 背景色。既定: 吹き出し `rgba(0, 154, 206, 0.9)` / inline `transparent`。
   * inline でカプセル風にしたい場合は明示的に指定する。
   */
  backgroundColor?: string;
  /** インライン配置にする（既定は絶対配置の吹き出し） */
  inline?: boolean;
  /** 任意の追加クラス（例: `bodytext-11`） */
  className?: string;
}

const InteractionContainer = styled.div<{ $inline: boolean }>`
  position: relative;
  display: ${({ $inline }) => ($inline ? "flex" : "inline-flex")};
  align-items: center;
  justify-content: center;
`;

// 配置とアニメーションで `transform` が競合するのを防ぐため、
// 配置を担う Positioner と、内側でアニメーションする Content を分離する。
// - Positioner: placement / offset に基づく absolute 位置 + `translateX/Y(-50%)`
// - Content: scale / translateY 等のアニメーション専用
// こうしておくと、bounce 中も Positioner の中央寄せが維持され、
// 「ボタン右下から正規位置へ飛んで戻る」現象を防げる。
const FloatingPositioner = styled.div<{
  $placement: "top" | "bottom" | "left" | "right";
  $offset: number;
  $inline: boolean;
}>`
  pointer-events: none;
  z-index: 999;

  ${({ $inline, $placement, $offset }) => {
    if ($inline) {
      return css`
        position: relative;
      `;
    }
    const placementCss = {
      top: css`
        bottom: calc(100% + ${$offset}px);
        left: 50%;
        transform: translateX(-50%);
      `,
      bottom: css`
        top: calc(100% + ${$offset}px);
        left: 50%;
        transform: translateX(-50%);
      `,
      left: css`
        right: calc(100% + ${$offset}px);
        top: 50%;
        transform: translateY(-50%);
      `,
      right: css`
        left: calc(100% + ${$offset}px);
        top: 50%;
        transform: translateY(-50%);
      `,
    }[$placement];
    return css`
      position: absolute;
      ${placementCss}
    `;
  }}
`;

const FloatingContent = styled.div<{
  $fontSize: string;
  $color: string;
  $backgroundColor: string;
  $isVisible: boolean;
  $animationType: MicroAnimationType;
  $inline: boolean;
}>`
  white-space: nowrap;
  font-size: ${(p) => p.$fontSize};
  color: ${(p) => p.$color};
  background-color: ${(p) => p.$backgroundColor};
  display: flex;
  align-items: center;
  gap: 4px;

  ${({ $inline, $backgroundColor }) => {
    if ($inline) {
      const hasBg =
        $backgroundColor !== "transparent" && $backgroundColor.length > 0;
      return css`
        text-align: center;
        ${hasBg
          ? css`
              padding: 2px 8px;
              border-radius: 12px;
            `
          : ""}
      `;
    }
    return css`
      padding: 2px 8px;
      border-radius: 12px;
      border: 1px solid #fff;
    `;
  }}

  ${({ $isVisible, $animationType }) =>
    $isVisible
      ? css`
          opacity: 1;
          ${microAnimationFor($animationType)}
        `
      : css`
          opacity: 0;
          transition: opacity 0.3s ease-out;
        `}
`;

const MicroInteraction: React.FC<MicroInteractionProps> = ({
  text,
  trigger,
  show,
  placement = "top",
  offset = 8,
  duration = 2000,
  animationType = "scale",
  icon,
  fontSize = "11px",
  color,
  backgroundColor,
  inline = false,
  className,
}) => {
  const [showText, setShowText] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const prevShowRef = useRef<boolean | undefined>(show);

  // 既定値はモードに合わせる。inline は通常ラベル風（プレーンテキスト）、
  // tooltip は青背景カプセル + 白文字。
  const resolvedColor =
    color ?? (inline ? "var(--soremo-blue)" : "#ffffff");
  const resolvedBackground =
    backgroundColor ?? (inline ? "transparent" : "rgba(0, 154, 206, 0.9)");

  // Controlled 起動: show が false → true に変わったタイミングで 1 回再生
  useEffect(() => {
    if (show === undefined) {
      prevShowRef.current = undefined;
      return;
    }
    if (show && !prevShowRef.current) {
      setShowText(true);
      setIsVisible(true);
    }
    prevShowRef.current = show;
  }, [show]);

  // duration タイマーで自動的にフェードアウト → DOM から除去
  useEffect(() => {
    if (!showText) return;
    setIsVisible(true);
    const hideTimer = window.setTimeout(
      () => setIsVisible(false),
      Math.max(0, duration - 300),
    );
    const removeTimer = window.setTimeout(
      () => setShowText(false),
      duration,
    );
    return () => {
      window.clearTimeout(hideTimer);
      window.clearTimeout(removeTimer);
    };
  }, [showText, duration]);

  // trigger click 起動: show 制御中は click では起動させない
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (trigger?.props?.onClick) {
      trigger.props.onClick(event);
    }
    if (show === undefined) {
      setShowText(true);
    }
  };

  const triggerWithHandler = trigger
    ? React.cloneElement(trigger, { onClick: handleClick })
    : null;

  return (
    <InteractionContainer $inline={inline}>
      {triggerWithHandler}
      {showText && (
        <FloatingPositioner
          $placement={placement}
          $offset={offset}
          $inline={inline}
        >
          <FloatingContent
            $fontSize={fontSize}
            $color={resolvedColor}
            $backgroundColor={resolvedBackground}
            $isVisible={isVisible}
            $animationType={animationType}
            $inline={inline}
            className={className}
          >
            {icon && (
              <span
                className="material-symbols-rounded"
                style={{ fontSize }}
              >
                {icon}
              </span>
            )}
            {text}
          </FloatingContent>
        </FloatingPositioner>
      )}
    </InteractionContainer>
  );
};

export default MicroInteraction;
